import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2 } from 'lucide-react';

// Computes the post-discount NET total for an order using the same precedence
// the rest of the app uses (item.discount_percent → product master discount →
// order.discount_percent → 10%). Matches `_order_net_total` on the backend and
// the WhatsApp/PDF/Excel net used elsewhere.
const computeOrderNet = (order, productDiscountByPid = {}) => {
  const orderPct = order.discount_percent == null ? 10 : Number(order.discount_percent);
  let net = 0;
  (order.items || []).forEach((it) => {
    const pct = it.discount_percent != null
      ? Number(it.discount_percent)
      : (productDiscountByPid[it.product_id] != null ? Number(productDiscountByPid[it.product_id]) : orderPct);
    net += Number(it.total || 0) * (1 - pct / 100);
  });
  return net;
};

export const CrmDealerDialog = ({ crmDealerDialog, onClose, setReviewOrder, products = [] }) => {
  const productDiscountByPid = React.useMemo(() => {
    const m = {};
    (products || []).forEach((p) => { if (p && p.id) m[p.id] = p.discount_percent; });
    return m;
  }, [products]);

  return (
    <Dialog open={!!crmDealerDialog} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto" data-testid="orders-crm-dealer-dialog">
        <DialogHeader>
          <DialogTitle>
            {crmDealerDialog?.dealer?.name || 'Dealer'}
            {crmDealerDialog?.dealer?.town ? <span className="ml-2 text-sm font-normal" style={{ color: '#5C6B61' }}>· {crmDealerDialog.dealer.town}</span> : null}
          </DialogTitle>
        </DialogHeader>
        {crmDealerDialog?._loading ? (
          <Loader2 className="w-6 h-6 animate-spin mx-auto my-6" />
        ) : crmDealerDialog?._error ? (
          <p className="text-sm" style={{ color: '#C05C35' }}>{crmDealerDialog._error}</p>
        ) : crmDealerDialog ? (
          <div className="space-y-5">
            {/* Outstanding invoices first */}
            <section>
              <h3 className="font-semibold mb-2" style={{ color: '#C05C35' }}>
                Outstanding Invoices ({(crmDealerDialog.outstanding_invoices || []).length})
              </h3>
              {(crmDealerDialog.outstanding_invoices || []).length === 0 ? (
                <p className="text-sm" style={{ color: '#5C6B61' }}>No outstanding bills on file.</p>
              ) : (
                <table className="w-full text-xs border">
                  <thead style={{ background: '#FBF9F6' }}>
                    <tr>
                      <th className="p-2 text-left">Date</th>
                      <th className="p-2 text-left">Txn No</th>
                      <th className="p-2 text-right">Net (₹)</th>
                      <th className="p-2 text-right">Due (₹)</th>
                      <th className="p-2 text-right">OD Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...crmDealerDialog.outstanding_invoices].sort((a, b) => b.od_days - a.od_days).map((inv, i) => (
                      <tr key={i} className="border-t" style={{ borderColor: '#E2DFD8' }}>
                        <td className="p-2">{inv.date}</td>
                        <td className="p-2">{inv.txn_no}</td>
                        <td className="p-2 text-right">{inv.net.toLocaleString('en-IN')}</td>
                        <td className="p-2 text-right font-medium" style={{ color: '#C05C35' }}>{inv.due.toLocaleString('en-IN')}</td>
                        <td className="p-2 text-right">{inv.od_days}d</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {/* Dispatches below */}
            <section>
              <h3 className="font-semibold mb-2" style={{ color: '#2B5339' }}>
                Dispatches ({(crmDealerDialog.orders || []).length})
              </h3>
              {(crmDealerDialog.orders || []).length === 0 ? (
                <p className="text-sm" style={{ color: '#5C6B61' }}>No dispatched/delivered orders.</p>
              ) : (
                <table className="w-full text-xs border">
                  <thead style={{ background: '#FBF9F6' }}>
                    <tr>
                      <th className="p-2 text-left">Dispatch Date</th>
                      <th className="p-2 text-left">Items</th>
                      <th className="p-2 text-right">Qty</th>
                      <th className="p-2 text-right">Net Amt</th>
                      <th className="p-2 text-left">Vehicle</th>
                      <th className="p-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...crmDealerDialog.orders].sort((a, b) => {
                      const da = ((a.dispatches || []).map(x => x.dispatched_at).filter(Boolean).sort()[0]) || a.updated_at || '';
                      const db = ((b.dispatches || []).map(x => x.dispatched_at).filter(Boolean).sort()[0]) || b.updated_at || '';
                      return String(db).localeCompare(String(da));
                    }).map((o) => {
                      const dispatchDate = (o.dispatches || []).map(x => x.dispatched_at).filter(Boolean).sort()[0];
                      const netAmt = computeOrderNet(o, productDiscountByPid);
                      return (
                      <tr key={o.id} className="border-t hover:bg-[#FBF9F6] transition-colors" style={{ borderColor: '#E2DFD8' }}>
                        <td className="p-2">{dispatchDate ? String(dispatchDate).slice(0, 10) : '-'}</td>
                        <td className="p-2">
                          <button
                            type="button"
                            // Don't close this CRM popup — let the review
                            // dialog stack ON TOP. When the user closes the
                            // review they return to this CRM popup, not all
                            // the way back to the Orders page (user spec:
                            // "always de-escalate one step only").
                            onClick={() => setReviewOrder(o)}
                            className="text-left hover:underline focus:outline-none"
                            style={{ color: '#2B5339' }}
                            title="Click to review this order"
                            data-testid="crm-dispatch-review-link"
                          >
                            {(o.items || []).map(i => `${i.product_name}x${i.quantity}`).join(', ')}
                          </button>
                        </td>
                        <td className="p-2 text-right">{(o.items || []).reduce((s, i) => s + i.quantity, 0)}</td>
                        <td className="p-2 text-right font-medium" data-testid="crm-dispatch-net-amt">{'\u20B9'}{netAmt.toFixed(2)}</td>
                        <td className="p-2">{o.vehicle_number || '-'}</td>
                        <td className="p-2 uppercase">{o.status}</td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              )}
            </section>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
